import React, { useRef } from 'react'
import { Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { Skeleton } from 'antd'
import { DirectiveRow } from '../DirectiveRow'
import { DailyRow } from './Rows/DailyRow'
import { MonthlyRow } from './Rows/MonthlyRow'
import { WeeklyRow } from './Rows/WeeklyRow'
import { AutoSizer, CellMeasurerCache, List, CellMeasurer } from 'react-virtualized'
import { useDirectiveContext } from '../../DirectiveContext'
import { MultiSortColumn } from '../Sorting/MultiSortColumn'
import { sortTypes } from '../../constants'
import moment from 'moment'
import { formatters } from '@utils/intl/dateTimeFormatters'

export const IntramonthGrid = ({
  isMonthlyLoading,
  monthlyDirectives,
  activeSort,
  sortDirection,
  setActiveSort,
  setSortDirection,
  updateRow,
  addPricesToRow,
}) => {
  const { selectedDate } = useDirectiveContext()
  const getDataRows = (viewingWeekly, directive) => {
    if (viewingWeekly) {
      return [
        {
          title: `${formatters.WEEKDAY_MONTH_DATE(moment(selectedDate))?.toUpperCase()}`,
          height: 30,
          RowComponent: DailyRow,
        },
        { title: 'WEEKLY', height: 30, RowComponent: WeeklyRow },
        { title: 'MONTHLY', height: 45, RowComponent: MonthlyRow },
      ]
    }
    return [
      {
        title: `${formatters.WEEKDAY_MONTH_DATE(moment(selectedDate))?.toUpperCase()}`,
        height: 30,
        RowComponent: DailyRow,
      },

      { title: 'MONTHLY', height: 45, RowComponent: MonthlyRow },
    ]
  }
  return (
    <>
      <GridHeader
        activeSort={activeSort}
        sortDirection={sortDirection}
        setActiveSort={setActiveSort}
        setSortDirection={setSortDirection}
      />
      <Horizontal className='bg-1' flex={1}>
        <Vertical flex={1}>
          {isMonthlyLoading ? (
            <>
              <Skeleton className='p-4' active />
              <Skeleton className='p-4' active />
              <Skeleton className='p-4' active />
              <Skeleton className='p-4' active />
              <Skeleton className='p-4' active />
            </>
          ) : (
            <>
              <DirectiveRows
                addPricesToRow={addPricesToRow}
                monthlyDirectives={monthlyDirectives}
                getDataRows={getDataRows}
                updateRow={updateRow}
              />
            </>
          )}
        </Vertical>
      </Horizontal>
    </>
  )
}
const DirectiveRows = ({ monthlyDirectives, getDataRows, updateRow, addPricesToRow }) => {
  const { viewingWeekly } = useDirectiveContext()
  const cache = new CellMeasurerCache({
    defaultHeight: 60,
    fixedWidth: true,
  })
  const listRef = useRef(null)
  const resizeRow = (index) => {
    cache.clear(index)
    listRef.current?.recomputeRowHeights(index)
  }

  return (
    <AutoSizer>
      {({ width, height }) => (
        <List
          deferredMeasurementCache={cache}
          height={height}
          rowCount={monthlyDirectives.length}
          rowHeight={cache.rowHeight}
          width={width}
          ref={listRef}
          overscanRowsCount={10}
          rowRenderer={({ index, key, parent, style }) => {
            return (
              <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                <div style={style}>
                  <DirectiveRow
                    resizeRow={() => resizeRow(index)}
                    directive={monthlyDirectives[index]}
                    dataRows={getDataRows(viewingWeekly, monthlyDirectives[index])}
                    updateRow={updateRow}
                    addPricesToRow={addPricesToRow}
                    isMonthly
                  />
                </div>
              </CellMeasurer>
            )
          }}
        />
      )}
    </AutoSizer>
  )
}

const GridHeader = ({ activeSort, setActiveSort, sortDirection, setSortDirection }) => {
  const changeSort = (sortType) => {
    if (!sortType) {
      setActiveSort(undefined)
      setSortDirection(undefined)
      return
    }
    if (sortType === activeSort) {
      if (!sortDirection) {
        setSortDirection('asc')
      } else if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else {
        setSortDirection(undefined)
      }
    } else {
      setSortDirection('asc')
    }
    setActiveSort(sortType)
  }
  return (
    <Horizontal flex={0} background='bg-2' border='bottom' className='px-3 directive-grid-header' verticalCenter>
      <MultiSortColumn
        title='DIRECTIVE'
        className='p-3 sortable'
        align='left'
        changeSort={changeSort}
        activeSort={activeSort}
        sortDirection={sortDirection}
        sortableItems={[
          { title: 'Terminal', value: sortTypes.TERMINAL.key },
          { title: 'Supplier', value: sortTypes.SUPPLIER.key },
          { title: 'Product', value: sortTypes.PRODUCT.key },
          { title: 'Selected Day Directive Delta', value: sortTypes.TOTAL.key },
        ]}
        flex={3}
      />
      <Vertical flex={4}>
        <Horizontal>
          <Vertical flex={1}>
            <Horizontal>
              <MultiSortColumn
                title='ALLOCATION'
                className='sortable  py-3'
                sortableItems={[
                  { title: 'Daily Alloc.', value: sortTypes.DAILYALLOCATION.key },
                  { title: 'Weekly Alloc.', value: sortTypes.WEEKLYALLOCATION.key },
                  { title: 'Monthly Alloc.', value: sortTypes.MONTHLYALLOCATION.key },
                ]}
                changeSort={changeSort}
                activeSort={activeSort}
                sortDirection={sortDirection}
              />
              <MultiSortColumn
                title='PERCENT'
                className='sortable  py-3'
                sortableItems={[
                  { title: 'Daily Percent', value: sortTypes.DAILYPERCENT.key },
                  { title: 'Weekly Percent', value: sortTypes.WEEKLYPERCENT.key },
                  { title: 'Monthly Percent', value: sortTypes.MONTHLYPERCENT.key },
                ]}
                changeSort={changeSort}
                activeSort={activeSort}
                sortDirection={sortDirection}
              />
              <MultiSortColumn
                title='START'
                className='sortable  py-3'
                sortableItems={[
                  { title: 'Daily Start', value: sortTypes.DAILYSTART.key },
                  { title: 'Weekly Start', value: sortTypes.WEEKLYSTART.key },
                  { title: 'Monthly Start', value: sortTypes.MONTHLYSTART.key },
                ]}
                changeSort={changeSort}
                activeSort={activeSort}
                sortDirection={sortDirection}
              />
            </Horizontal>
          </Vertical>
          <MultiSortColumn
            title='PACE'
            className='sortable  py-3'
            sortableItems={[
              { title: 'Monthly Pace', value: sortTypes.MONTHLYPACE.key },
              { title: 'Contract Pace', value: sortTypes.CONTRACTPACE.key },
            ]}
            changeSort={changeSort}
            activeSort={activeSort}
            sortDirection={sortDirection}
            align='left'
          />
        </Horizontal>
      </Vertical>
    </Horizontal>
  )
}
